import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import * as Sentry from "@sentry/react";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import RouteTracker from "./components/RouteTracker";

// Marketing pages — kept eager so the landing page renders without a flash.
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ScrollToTop from "./components/ScrollToTop";

// Everything else is route-split: ~55 KB of admin + ~30 KB of heavy dashboard
// (MealPlan, Nutrition, ExerciseActive) move out of main.js. Each becomes its
// own chunk loaded only when a logged-in user navigates to that page.
const Services = lazy(() => import("./pages/Services"));
const About = lazy(() => import("./pages/About"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PremiumSupport = lazy(() => import("./pages/PremiumSupport"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Quiz = lazy(() => import("./pages/Quiz"));
const QuizResult = lazy(() => import("./pages/QuizResult"));
const Recipe = lazy(() => import("./pages/Recipe"));
const ConditionLanding = lazy(() => import("./pages/ConditionLanding"));

const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const DashboardHome = lazy(() => import("./pages/dashboard/DashboardHome"));
const Profile = lazy(() => import("./pages/dashboard/Profile"));
const Targets = lazy(() => import("./pages/dashboard/Targets"));
const Insights = lazy(() => import("./pages/dashboard/Insights"));
const Nutrition = lazy(() => import("./pages/dashboard/Nutrition"));
const MealPlan = lazy(() => import("./pages/dashboard/MealPlan"));
const Chat = lazy(() => import("./pages/dashboard/Chat"));
const Exercise = lazy(() => import("./pages/dashboard/Exercise"));
const ExerciseLibrary = lazy(() => import("./pages/dashboard/ExerciseLibrary"));
const ExerciseLibraryExercises = lazy(() => import("./pages/dashboard/ExerciseLibraryExercises"));
const ExercisePlanPreview = lazy(() => import("./pages/dashboard/ExercisePlanPreview"));
const ExerciseActive = lazy(() => import("./pages/dashboard/ExerciseActive"));
const ExerciseCustomBuilder = lazy(() => import("./pages/dashboard/ExerciseCustomBuilder"));
const Sleep = lazy(() => import("./pages/dashboard/Sleep"));
const Stress = lazy(() => import("./pages/dashboard/Stress"));
const Referral = lazy(() => import("./pages/dashboard/Referral"));
const Messages = lazy(() => import("./pages/dashboard/Messages"));
const MealPlanHistory = lazy(() => import("./pages/dashboard/MealPlanHistory"));
const Billing = lazy(() => import("./pages/dashboard/Billing"));

const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminCoaches = lazy(() => import("./pages/admin/AdminCoaches"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminUsage = lazy(() => import("./pages/admin/AdminUsage"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminDemoAccounts = lazy(() => import("./pages/admin/AdminDemoAccounts"));
const AdminDemoPersonas = lazy(() => import("./pages/admin/AdminDemoPersonas"));
const AdminStaff = lazy(() => import("./pages/admin/AdminStaff"));
const AdminLLMCost = lazy(() => import("./pages/admin/AdminLLMCost"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const AdminOrchestrationConsole = lazy(() => import("./pages/admin/AdminOrchestrationConsole"));
const AdminTools = lazy(() => import("./pages/admin/AdminTools"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminOps = lazy(() => import("./pages/admin/AdminOps"));

const CoachLayout = lazy(() => import("./layouts/CoachLayout"));
const CoachDashboard = lazy(() => import("./pages/coach/CoachDashboard"));
const CoachClients = lazy(() => import("./pages/coach/CoachClients"));
const CoachClientDetail = lazy(() => import("./pages/coach/CoachClientDetail"));
const CoachMessages = lazy(() => import("./pages/coach/CoachMessages"));
const CoachOverridePlan = lazy(() => import("./pages/coach/CoachOverridePlan"));

const StaffLayout = lazy(() => import("./layouts/StaffLayout"));

const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy"));
const Disclaimer = lazy(() => import("./pages/legal/Disclaimer"));

function ErrorFallback() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9] px-6" data-testid="sentry-error-fallback">
            <div className="max-w-md text-center">
                <h1 className="font-heading text-4xl text-stone-900 tracking-tighter mb-3">Something went wrong.</h1>
                <p className="text-stone-600 mb-6">We've been notified and are looking into it. Please refresh the page to try again.</p>
                <button onClick={() => window.location.reload()} className="bg-stone-900 text-white rounded-full px-6 py-3 font-medium hover:-translate-y-0.5 transition-transform" data-testid="sentry-error-retry">Reload</button>
            </div>
        </div>
    );
}

// Suspense fallback for lazy-loaded routes. Plain background — visible for the
// few-hundred-ms it takes to fetch a code chunk. Deliberately no spinner; the
// fast path is invisible.
function RouteFallback() {
    return <div className="min-h-screen bg-[#FAFAF9]" data-testid="route-suspense-fallback" aria-hidden="true" />;
}

function App() {
    return (
        <div className="App">
            <HelmetProvider>
            <BrowserRouter>
                <AuthProvider>
                    <Toaster position="top-right" richColors closeButton />
                    <ScrollToTop />
                    <RouteTracker />
                    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
                    <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/platform" element={<Services />} />
                        <Route path="/services" element={<Navigate to="/platform" replace />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/premium-support" element={<PremiumSupport />} />
                        <Route path="/billing/success" element={<BillingSuccess />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        <Route path="/r/:slug" element={<Recipe />} />
                        {/* Condition landing pages — referenced from the Home
                            condition picker. Single component, route param
                            switches the content. Replace with bespoke pages
                            per the Energize.fit Emergent Developer Brief. */}
                        <Route path="/pcos" element={<ConditionLanding />} />
                        <Route path="/diabetes" element={<ConditionLanding />} />
                        <Route path="/thyroid" element={<ConditionLanding />} />
                        <Route path="/hypertension" element={<ConditionLanding />} />
                        <Route path="/weight-loss" element={<ConditionLanding />} />
                        <Route path="/post-pregnancy" element={<ConditionLanding />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/quiz" element={<Quiz />} />
                        <Route path="/quiz/result/:id" element={<QuizResult />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/terms-of-service" element={<TermsOfService />} />
                        <Route path="/refund-policy" element={<RefundPolicy />} />
                        <Route path="/disclaimer" element={<Disclaimer />} />
                        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

                        <Route
                            path="/app"
                            element={
                                <ProtectedRoute>
                                    <DashboardLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<DashboardHome />} />
                            <Route path="profile" element={<Profile />} />
                            <Route path="targets" element={<Targets />} />
                            <Route path="insights" element={<Insights />} />
                            <Route path="meal-plan" element={<MealPlan />} />
                            <Route path="meal-plan/history" element={<MealPlanHistory />} />
                            <Route path="meal-plan/:planId" element={<MealPlan />} />
                            <Route path="nutrition" element={<Nutrition />} />
                            <Route path="chat" element={<Chat />} />
                            <Route path="exercise" element={<Exercise />} />
                            <Route path="exercise/library" element={<ExerciseLibrary />} />
                            <Route path="exercise/library/exercises" element={<ExerciseLibraryExercises />} />
                            <Route path="exercise/plan/:id" element={<ExercisePlanPreview />} />
                            <Route path="exercise/active" element={<ExerciseActive />} />
                            <Route path="exercise/custom" element={<ExerciseCustomBuilder />} />
                            <Route path="sleep" element={<Sleep />} />
                            <Route path="stress" element={<Stress />} />
                            <Route path="referral" element={<Referral />} />
                            <Route path="messages" element={<Messages />} />
                            <Route path="billing" element={<Billing />} />
                        </Route>

                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<AdminDashboard />} />
                            <Route path="users" element={<AdminUsers />} />
                            <Route path="users/:id" element={<AdminUserDetail />} />
                            <Route path="leads" element={<AdminLeads />} />
                            <Route path="coaches" element={<AdminCoaches />} />
                            <Route path="payments" element={<AdminPayments />} />
                            <Route path="usage" element={<AdminUsage />} />
                            <Route path="llm-cost" element={<AdminLLMCost />} />
                            <Route path="referrals" element={<AdminReferrals />} />
                            <Route path="coupons" element={<AdminCoupons />} />
                            <Route path="ops" element={<AdminOps />} />
                            <Route path="contacts" element={<AdminContacts />} />
                            <Route path="feedback" element={<AdminFeedback />} />
                            <Route path="settings" element={<AdminSettings />} />
                            <Route path="demo" element={<AdminDemoAccounts />} />
                            <Route path="personas" element={<AdminDemoPersonas />} />
                            <Route path="orchestration" element={<AdminOrchestrationConsole />} />
                            <Route path="tools" element={<AdminTools />} />
                            <Route path="staff" element={<AdminStaff />} />
                        </Route>

                        <Route path="/staff" element={<ProtectedRoute><StaffLayout /></ProtectedRoute>}>
                            <Route index element={<AdminDemoAccounts />} />
                        </Route>

                        <Route path="/coach" element={<CoachLayout />}>
                            <Route index element={<CoachDashboard />} />
                            <Route path="clients" element={<CoachClients />} />
                            <Route path="clients/:id" element={<CoachClientDetail />} />
                            <Route path="clients/:id/meal-plan" element={<CoachOverridePlan />} />
                            <Route path="messages" element={<CoachMessages />} />
                        </Route>
                    </Routes>
                    </Suspense>
                    </Sentry.ErrorBoundary>
                </AuthProvider>
            </BrowserRouter>
            </HelmetProvider>
        </div>
    );
}

export default App;
